/**
 * KnowledgePageHead — the gradient page-head band under the breadcrumb on
 * every Knowledge Center sub-page (article, release notes, ...). `meta` is
 * caller-composed so each page controls its own byline content; `MetaItem`
 * and `MetaPill` are exported so callers build that byline consistently.
 */

import type { ReactNode } from 'react';
import styled from 'styled-components';
import { media } from '../../../../styles/media';

export interface KnowledgePageHeadProps {
  overline?: string;
  title: string;
  lede?: string;
  meta?: ReactNode;
}

const Head = styled.div`
  background: ${({ theme }) => theme.colors.gradients.knowledgeHero};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const Wrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px ${({ theme }) => theme.spacing[8]} 26px;

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[4]};
  }
`;

const Overline = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  text-transform: uppercase;
  letter-spacing: 0.09em;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary.base};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: 30px;
  line-height: 1.2;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  letter-spacing: -0.02em;
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
  color: ${({ theme }) => theme.colors.primary.dark};
  max-width: 760px;
`;

const Lede = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.body};
  margin: 0;
  max-width: 640px;
`;

const Byline = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing[4]};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.body};
`;

/** One icon + text entry in the page-head byline. */
export const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};

  svg {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.primary.dark};
  }
`;

/** Audience pill in the page-head byline. */
export const MetaPill = styled.span`
  display: inline-block;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.primary.mist};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  padding: ${({ theme }) => theme.spacing[1]} 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

export function KnowledgePageHead({ overline, title, lede, meta }: KnowledgePageHeadProps) {
  return (
    <Head>
      <Wrap>
        {overline && <Overline>{overline}</Overline>}
        <Title>{title}</Title>
        {lede && <Lede>{lede}</Lede>}
        {meta && <Byline>{meta}</Byline>}
      </Wrap>
    </Head>
  );
}

export default KnowledgePageHead;
